import { Component, Inject, OnInit, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../core';
import { ConfigurationApi, GovernanceActivities, LookupApi, LookupField, OfficeEntity } from '../core/api.gen';
import { LoaderService } from '../core/loader.service';
import { LookupTypeKeys } from '../shared/lookup-type-keys';

export interface GovernanceActivity {
  id: number;
  activityType: number;
  order: number;
  header: string;
  description: string;
  isActive: boolean;
  countryId?: number;
  locationId?: number;
  legalEntityId?: number;
  documentTypeId?: number;
  documentConfigId?: number;
  documentConfig?: {
    countryId?: number;
    locationId?: number;
    legalEntityId?: number;
    documentTypeId?: number;
  };
  activityTypeString?: string;
}

@Component({
  selector: 'eos-governance-activities',
  templateUrl: './governance-activities.component.html',
  styleUrls: ['./governance-activities.component.scss']
})
export class GovernanceActivitiesComponent implements OnInit {
  activities: GovernanceActivities[] = [];
  allActivities: GovernanceActivities[] = [];
  isAscendic = false;

  activityFilter = new UntypedFormGroup({
    country: new UntypedFormControl(null),
    officeLocation: new UntypedFormControl(null),
    legalEntity: new UntypedFormControl(null),
    isActive: new UntypedFormControl(false),
  });

  countries = [];
  officeLocations: LookupField[] = [];
  filteredOfficeLocations: LookupField[] = [];
  legalEntities: OfficeEntity[] = [];
  filteredLegalEntities: OfficeEntity[] = [];

  activityTypes = [
    { id: 100, displayText: 'Email' },
    { id: 101, displayText: 'Profile update' },
    { id: 102, displayText: 'External' },
    { id: 104, displayText: 'Document upload' },
  ];

  constructor(
    private lookupApi: LookupApi,
    private loader: LoaderService,
    private toast: ToastService,
    private router: Router,
    private configApi: ConfigurationApi,
  ) {}

  ngOnInit(): void {
    this.loadFilterData();
  }

  loadFilterData() {
    this.loader.show();
    forkJoin({
      countries: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.OFFICE_LOCATION_COUNTRY]),
      officeLocations: this.lookupApi.getLookupFieldsByTypes([LookupTypeKeys.OFFICE_LOCATION]),
      entities: this.lookupApi.getOfficeEntities(undefined, undefined)
    })
    .pipe(finalize(() => this.loader.clear()))
    .subscribe(({ countries, officeLocations, entities }) => {
      this.countries = countries;
      this.officeLocations = officeLocations;
      this.legalEntities = entities;
      this.getActivities();
    });
  }

  onCountryChange(event: any) {
    this.activityFilter.controls.officeLocation.reset();
    this.activityFilter.controls.legalEntity.reset();
    this.filteredOfficeLocations = event
      ? this.officeLocations.filter(l => l.parentFieldId === event.id)
      : [];
    this.filteredLegalEntities = event
      ? this.legalEntities.filter(e => e.parentFieldId === event.id)
      : [];
  }

  onOfficeLocationChange(event: any) {
    this.activityFilter.controls.legalEntity.reset();
    const countryId = this.activityFilter.controls.country.value?.id;
    this.filteredLegalEntities = countryId
      ? this.legalEntities.filter(e => e.parentFieldId === countryId)
      : [];
  }

  onSubmit() {
    this.getActivities();
  }

  getActivities() {
    if (this.activityFilter.valid) {
      this.loader.show();
      const { country, officeLocation, legalEntity } = this.activityFilter.getRawValue();
      this.configApi.getGovernanceActivities()
        .pipe(finalize(() => this.loader.clear()))
        .subscribe(resp => {
          let filtered = resp;
          // Common activities (no document config) apply to every scope, so keep them under any filter.
          const isCommon = (a: GovernanceActivities) => !a.documentConfigId;
          if (country) filtered = filtered.filter(a => isCommon(a) || a.documentConfig?.countryId === country.id);
          if (officeLocation) filtered = filtered.filter(a => isCommon(a) || a.documentConfig?.locationId === officeLocation.id);
          if (legalEntity) filtered = filtered.filter(a => isCommon(a) || a.documentConfig?.legalEntityId === legalEntity.id);
          filtered.forEach(act => {
            (act as any).activityTypeString = this.activityTypes.find(a => a.id === act.activityType)?.displayText;
          });
          this.allActivities = filtered;
          this.activities = [...filtered].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          this.isAscendic = true;
        });
    }
  }

  sort() {
    if (!this.isAscendic) {
      this.activities = [...this.allActivities].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      this.isAscendic = true;
    } else {
      this.activities = [...this.allActivities].sort((a, b) => (b.order ?? 0) - (a.order ?? 0));
      this.isAscendic = false;
    }
  }

  updateOrder(act: GovernanceActivities) {
    if (act.isActive && act.order <= 0) {
      this.toast.warning('Order should be greater than zero for an active activity');
      return;
    }
    this.loader.show();
    this.configApi.saveGovernanceActivities(act)
      .pipe(finalize(() => this.loader.clear()))
      .subscribe(
        () => this.toast.success('Order successfully updated'),
        () => this.toast.error('An error occurred. Please try again.')
      );
  }

  editActivity(activity: GovernanceActivities) {
    this.router.navigate(['configure-governance-activities/edit', activity.id]);
  }

  createActivity() {
    this.router.navigateByUrl('configure-governance-activities/create');
  }

  clearForm() {
    this.activityFilter.reset();
    this.activities = [];
    this.allActivities = [];
    this.filteredOfficeLocations = [];
    this.filteredLegalEntities = [];
  }
  onChange(checked: boolean) {
    this.activities = checked
      ? this.allActivities.filter(a => a.isActive)
      : [...this.allActivities];
  }
}